'use client';

import { PrimaryButtonMTV2, RoundedFilledButton, SecondaryButtonMT } from '@/components/buttons/Buttons';
import TabViewContainer from '@/components/tabs/TabViewContainer';
import { Card } from 'primereact/card';
import { ProgressBar } from 'primereact/progressbar';
import { useState } from 'react';
import { MdQueue } from 'react-icons/md';
import RequestBudgetDialog, { CreateBudgetRequest } from '../components/dialogs/RequestBudgetDialog';
import RequestsTable from '../components/tables/RequestsTable';
import TransactionsTable from '../components/tables/TransactionsTable';
import styles from './budget-management.module.css';
import { formatCurrency, GenericResponse, showTypedModal2 } from '@/common/common_utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '@/services/axios.service';
import { ENDPOINTS } from '@/constants/endpoints';
import { useAuthenticationStore } from '@/store';
import { X4bUserRoleEnum } from '@/constants/roles';

export interface BudgetSummaryDto {
	CurrentMonthSpending: number,
	BudgetRemaining: number,
	Limit: number
}

const BudgetManagementScreen = () => {
	const queryClient = useQueryClient();
	const [refreshKey, setRefreshKey] = useState(0);
	const hasRoles = useAuthenticationStore((state) => state.hasRoles);
	const canRequestBudget = hasRoles([X4bUserRoleEnum.User]);

	const budgetSummaryQ = useQuery({
		queryKey: ['Budget.GetBudgetSummary', refreshKey],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<BudgetSummaryDto>>(
				ENDPOINTS['/api/Budget/GetBudgetSummary'], {}
			);
			return result.data.Data;
		},
	});
	const progressPercent = budgetSummaryQ.data
		? budgetSummaryQ.data.CurrentMonthSpending / (budgetSummaryQ.data.CurrentMonthSpending + budgetSummaryQ.data.BudgetRemaining)
		: 0;
	return (
		<div>
			<h1 className="font-semibold text-primary text-headline-xx-large">
				Budget Management
			</h1>

			<div className="flex flex-col md:flex-row md:justify-between md:items-center gap-5 mt-6 w-full">
				<h2 className="font-medium text-primary text-headline-medium">
					Account summary
				</h2>
				{canRequestBudget && (
					<SecondaryButtonMT
						id="request-budget"
						className={`${styles.requestBudgetButton} secondary`}
						label="Request Budget"
						icon={() => <MdQueue></MdQueue>}
						onClick={async () => {
							const res = await showTypedModal2<{}, CreateBudgetRequest>(RequestBudgetDialog, {});
							// Trigger refetch of RequestsTable
							setRefreshKey(prev => prev + 1);
						}}
					/>)}

			</div>

			<Card
				className={styles.card}
				pt={{
					body: {
						className: styles.cardBody,
					},
				}}
			>
				<div className="flex flex-col gap-5 w-full md:flex-row md:justify-between font-poppins">

					<div>
						<div className="md:text-left mb-2">
							Budget remaining
						</div>
						<div className="text-primary font-medium text-[44px]">
							{formatCurrency(budgetSummaryQ.data?.BudgetRemaining)}
						</div>
					</div>
					<div>
						<div className="text-black mb-2">
							Current month spending
						</div>
						<div className="text-primary font-medium text-[44px]">
							{formatCurrency(budgetSummaryQ.data?.CurrentMonthSpending)}
						</div>
					</div>
				</div>

				<ProgressBar
					className={styles.progressBarContainer}
					value={(1 - progressPercent) * 100}
					showValue={false}
					pt={{
						value: {
							className: styles.progressBar,
						},
					}}
				/>

				<div className="flex justify-end items-start gap-2">
					<div className="text-base">Limit</div>
					<div className="text-primary font-medium text-[32px] leading-10">
						{formatCurrency(budgetSummaryQ.data?.Limit)}
					</div>
				</div>
			</Card>

			<TabViewContainer className="mt-6" tabList={[
				{
					tabName: 'Transactions',
					tabContent: <TransactionsTable />,
				},
				{
					tabName: 'Requests',
					tabContent: <RequestsTable
						refetchTrigger={refreshKey}
						onActionComplete={() => {
							setRefreshKey(prev => prev + 1);
						}}
					/>,
				},
			]} />
		</div>
	);
};

export default BudgetManagementScreen;
